
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useInvoiceViewer } from '@/hooks/useInvoiceViewer';
import LoadingState from '@/components/invoice-viewer/LoadingState';
import NotFoundState from '@/components/invoice-viewer/NotFoundState';
import InvoiceHeader from '@/components/invoice-viewer/InvoiceHeader';
import LineItems from '@/components/invoice-viewer/LineItems';
import PaymentSection from '@/components/invoice-viewer/PaymentSection';
import CustomerDetails from '@/components/invoice-viewer/CustomerDetails';

interface InvoiceViewerProps {
  previewMode?: boolean;
}

const InvoiceViewer: React.FC<InvoiceViewerProps> = ({ previewMode = false }) => {
  const { id } = useParams<{ id: string }>();
  const {
    invoice,
    loading,
    selectedPaymentMethod,
    handlePaymentMethodSelect,
    handlePaymentSubmit,
    showProcessingFee,
    getProcessingFee,
    chargeAchFees
  } = useInvoiceViewer(previewMode ? 'SAMPLE001' : id);

  const formatCurrency = (value: number): string => {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-10">
      <div className="container mx-auto px-4">
        {!previewMode ? (
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
        ) : (
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
        )}
        
        {loading ? (
          <LoadingState />
        ) : invoice ? (
          <div className="max-w-3xl mx-auto">
            <InvoiceHeader invoice={invoice} formatCurrency={formatCurrency} />
            
            <Card className="rounded-t-none border-t-0 shadow-lg">
              <CardContent className="p-6 md:p-8">
                <LineItems invoice={invoice} formatCurrency={formatCurrency} />
                
                {/* Payment Section */}
                {(invoice.status === 'pending' || previewMode) && (
                  <PaymentSection
                    invoice={invoice}
                    selectedPaymentMethod={selectedPaymentMethod}
                    handlePaymentMethodSelect={handlePaymentMethodSelect}
                    handlePaymentSubmit={handlePaymentSubmit}
                    showProcessingFee={showProcessingFee}
                    getProcessingFee={getProcessingFee}
                    chargeAchFees={chargeAchFees}
                    formatCurrency={formatCurrency}
                  />
                )}
                
                <CustomerDetails invoice={invoice} />
              </CardContent>
            </Card>
          </div>
        ) : (
          <NotFoundState />
        )}
      </div>
    </div>
  );
};

export default InvoiceViewer;
